import React, { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';

const PASSWORD_RULES = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

function validatePassword(password: string) {
  const errors: string[] = [];
  if (password.length < PASSWORD_RULES.minLength) errors.push(`At least ${PASSWORD_RULES.minLength} characters`);
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) errors.push('One uppercase letter');
  if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(password)) errors.push('One lowercase letter');
  if (PASSWORD_RULES.requireNumbers && !/[0-9]/.test(password)) errors.push('One number');
  if (PASSWORD_RULES.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character');
  return { isValid: errors.length === 0, errors };
}

export const ActivateAccountScreen: React.FC<{ uid: string; token: string }> = ({ uid, token }) => {
  const { activateAccount } = useAuth();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ pw1: false, pw2: false });

  const validation = useMemo(() => validatePassword(pw1), [pw1]);
  const passwordsMatch = pw1 && pw2 && pw1 === pw2;
  const canSubmit = Boolean(uid && token && validation.isValid && passwordsMatch && !loading);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      await activateAccount(uid, token, pw1);
      window.history.replaceState({}, '', '/');
    } catch (err: any) {
      const data = err?.data || {};
      const message =
        (Array.isArray(data.new_password) ? data.new_password.join(', ') : data.new_password) ||
        (Array.isArray(data.token) ? data.token.join(', ') : data.token) ||
        err?.message ||
        'Could not activate this account.';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-brand">
          <span className="tag">Account activation</span>
          <h1>Activate your AVCS account</h1>
          <p>Choose your password to finish setting up your account.</p>
        </div>
        <form onSubmit={submit} className="login-form" aria-label="Activate account form">
          {error && <div className="login-error">{error}</div>}
          <label>
            <span>New password</span>
            <input
              type="password"
              value={pw1}
              onChange={(event) => setPw1(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, pw1: true }))}
              autoComplete="new-password"
              placeholder="Create a password"
            />
          </label>
          {touched.pw1 && validation.errors.length > 0 && (
            <p className="text-sm text-avcs-muted">Missing: {validation.errors.join(', ')}</p>
          )}
          <label>
            <span>Confirm password</span>
            <input
              type="password"
              value={pw2}
              onChange={(event) => setPw2(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, pw2: true }))}
              autoComplete="new-password"
              placeholder="Re-enter password"
            />
          </label>
          {touched.pw2 && pw2 && !passwordsMatch && (
            <p className="text-sm text-red-600">Passwords do not match.</p>
          )}
          <button className="login-primary-btn" type="submit" disabled={!canSubmit}>
            {loading ? 'Activating...' : 'Activate account'}
          </button>
        </form>
      </section>
    </main>
  );
};
